import { isEmpty } from 'lodash';
import { createContext, useContext, useMemo, useState } from 'react';
import { useQueryFetch } from 'sonata-react-components';

import { getScreenInventory } from '@/services/dooh.service';

import type {
  ScreenInventoryFilters,
  ScreenInventoryFiltersRequest,
  ScreenInventoryItem,
  ScreenInventoryVolumetry
} from 'sonata-static-storage/lib/common/common.types';

type Context = {
  clearFilters: () => void;
  filters: Partial<ScreenInventoryFiltersRequest>;
  inventory?: ScreenInventoryItem[];
  inventoryByCountry?: ScreenInventoryItem[];
  isInventoryLoading: boolean;
  screensByCountry: {
    all: {
      [key: string]: number;
    };
    selection: {
      [key: string]: number;
    };
  };
  selectedInventoryIds: ScreenInventoryItem['itemId'][];
  selectedVolumetry?: ScreenInventoryVolumetry;
  setFilters: (filterName: keyof ScreenInventoryFilters, value: any) => void;
  setToggleSelectedItem: (itemId: ScreenInventoryItem['itemId']) => void;
  volumetry?: ScreenInventoryVolumetry;
};

type Props = {
  children: React.ReactElement;
};

type State = {
  filters: Context['filters'];
  selectedInventoryIds: Context['selectedInventoryIds'];
};

function enrichInventory(inventory: ScreenInventoryItem[] = []): ScreenInventoryItem[] {
  return inventory?.map(item => {
    const { numberOfScreens } = item;
    const screenForVenueTypes = (item.venueTypes.details || []).reduce((acc, d) => acc + d.totalPois, 0);
    if (screenForVenueTypes < numberOfScreens) {
      item.venueTypes.details.push({ id: 0, name: 'N/A', totalPois: numberOfScreens - screenForVenueTypes });
    }
    return item;
  });
}

function filterScreensInventoryItems(
  items: ScreenInventoryItem[],
  filters: Partial<ScreenInventoryFiltersRequest>
): ScreenInventoryItem[] {
  if (Object.keys(filters).length === 0) {
    return items;
  }

  return items.filter(item => {
    if (filters.name && !item.publisher.toLowerCase().includes(filters.name.trim().toLowerCase())) {
      return false;
    }
    if (filters.country && !filters.country.includes(item.country)) {
      return false;
    }
    if (filters.format && !filters.format.some(f => item.allowedAdTypes.details.some(d => String(d.id) === f))) {
      return false;
    }
    if (filters.venueType && !filters.venueType.some(vt => item.venueTypes.details.some(d => String(d.id) === vt))) {
      return false;
    }
    if (filters.adExchange && !filters.adExchange.some(adExchange => item.adExchangeId === adExchange)) {
      return false;
    }
    if (filters.publisher && !filters.publisher.some(publisher => item.publisherId === publisher)) {
      return false;
    }
    return true;
  });
}

function getScreensInventoryVolumetry(inventory: ScreenInventoryItem[]): ScreenInventoryVolumetry {
  const inventoryRawVolumetryFields = inventory.reduce(
    (acc: Record<string, any>, inventoryItem: ScreenInventoryItem) => {
      return {
        adExchangeIds: [...acc.adExchangeIds, inventoryItem.adExchangeId],
        adExchanges: [...acc.adExchanges, inventoryItem.adExchange],
        allowedAdTypes: [...acc.allowedAdTypes, ...(inventoryItem?.allowedAdTypes?.details || []).map(d => d.name)],
        countries: [...acc.countries, inventoryItem.country],
        publisherIds: [...acc.publisherIds, inventoryItem.publisherId],
        publishers: [...acc.publishers, inventoryItem.publisher],
        screenTypes: [...acc.screenTypes, ...(inventoryItem.screenTypes.details || []).map(d => d.name)],
        screens: acc.screens + inventoryItem.numberOfScreens,
        venueTypes: [...acc.venueTypes, ...(inventoryItem.venueTypes.details || []).map(d => d.name)]
      };
    },
    {
      adExchangeIds: [],
      adExchanges: [],
      allowedAdTypes: [],
      countries: [],
      publisherIds: [],
      publishers: [],
      screenTypes: [],
      screens: 0,
      venueTypes: []
    }
  );

  return {
    adExchangeIds: [...new Set(inventoryRawVolumetryFields.adExchangeIds)] as string[],
    adExchanges: new Set(inventoryRawVolumetryFields.adExchanges).size,
    allowedAdTypes: [...new Set(inventoryRawVolumetryFields.allowedAdTypes)] as string[],
    countries: new Set(inventoryRawVolumetryFields.countries).size,
    countryCodes: [...new Set(inventoryRawVolumetryFields.countries)] as string[],
    publisherIds: [...new Set(inventoryRawVolumetryFields.publisherIds)] as string[],
    publishers: new Set(inventoryRawVolumetryFields.publisherIds).size,
    screenTypes: new Set(inventoryRawVolumetryFields.screenTypes).size,
    screens: inventoryRawVolumetryFields.screens,
    venueTypes: new Set(inventoryRawVolumetryFields.venueTypes).size
  };
}

const DoohInventoryContext = createContext<Context | null>(null);

export function DoohInventoryProvider({ children }: Readonly<Props>) {
  const [{ filters, selectedInventoryIds }, setState] = useState<State>({
    filters: {},
    selectedInventoryIds: []
  });

  const { data, isLoading: isInventoryLoading } = useQueryFetch(['doohInventory'], getScreenInventory, {
    onSuccess: enrichInventory
  });

  const allInventory = data || [];
  const filteredInventory = filterScreensInventoryItems(allInventory, filters).map(item => ({
    ...item,
    selected: selectedInventoryIds.includes(item.itemId)
  }));

  const screensByCountry: Context['screensByCountry'] = {
    all: {},
    selection: {}
  };

  filteredInventory.forEach(f => {
    if (selectedInventoryIds.length) {
      if (f.selected) {
        screensByCountry.selection[f.country] =
          typeof screensByCountry.selection[f.country] === 'number'
            ? screensByCountry.selection[f.country] + f.numberOfScreens
            : f.numberOfScreens;
      }
    }
    screensByCountry.all[f.country] =
      typeof screensByCountry.all[f.country] === 'number'
        ? screensByCountry.all[f.country] + f.numberOfScreens
        : f.numberOfScreens;
  });

  const volumetry = getScreensInventoryVolumetry(filteredInventory);
  const selectedVolumetry = getScreensInventoryVolumetry(
    filteredInventory.filter(e => selectedInventoryIds.includes(e.itemId))
  );

  const setFilters = (filterName: keyof ScreenInventoryFilters, value: any) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...(filterName !== 'country' && { ...prev?.filters }),
        [filterName]: !isEmpty(value) ? value : undefined
      },
      selectedInventoryIds: []
    }));
  };

  const clearFilters = () => {
    setState(prev => ({
      ...prev,
      filters: {}
    }));
  };

  const setToggleSelectedItem = (itemId: ScreenInventoryItem['itemId']) => {
    const newSelected = selectedInventoryIds.includes(itemId)
      ? selectedInventoryIds.filter(id => id !== itemId)
      : [...selectedInventoryIds, itemId];
    setState(prev => ({
      ...prev,
      selectedInventoryIds: newSelected
    }));
  };

  const providerValue = useMemo(
    () => ({
      clearFilters,
      filters,
      inventory: filteredInventory,
      inventoryByCountry: filters.country?.length
        ? allInventory.filter(item => filters.country?.includes(item.country))
        : allInventory,
      isInventoryLoading,
      screensByCountry,
      selectedInventoryIds,
      selectedVolumetry,
      setFilters,
      setToggleSelectedItem,
      volumetry
    }),
    [allInventory, data, filters, selectedInventoryIds]
  );

  return <DoohInventoryContext.Provider value={providerValue}>{children}</DoohInventoryContext.Provider>;
}

export function useDoohInventory(): Context {
  const context = useContext(DoohInventoryContext);
  if (!context) {
    throw new Error('The hook useDoohInventory cannot be used outside the DoohInventoryProvider component');
  }

  return context;
}
